/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useCallback, useEffect, useState } from 'react';
import Media from 'react-media';

import { useLocation, useNavigate } from 'js/lib/useRouter';
import { get as userGet } from 'js/lib/user';

import { Grid, PageGridContainer, Typography2, breakpoints } from '@coursera/cds-core';

import CoachIcon from 'bundles/ai-coach-platform/components/branding/CoachIcon';
import useIsInLegalConsolidationFlowAndHasTouQueryParam from 'bundles/authentication/shared/hooks/useIsInLegalConsolidationFlowAndHasTouQueryParam';
import {
  useIsLearnerProfileLoading,
  useSkillRoles,
} from 'bundles/page-config-common/contexts/consumer/LearnerProfileContext';
import useUserAgent from 'bundles/page/hooks/useUserAgent';
import inServerContext from 'bundles/ssr/util/inServerContext';
import ShimmerFourCards from 'bundles/unified-home-common/components/loading-shimmer/ShimmerFourCards';
import ShimmerFourCardsMobile from 'bundles/unified-home-common/components/loading-shimmer/ShimmerFourCardsMobile';
import ShimmerOneCard from 'bundles/unified-home-common/components/loading-shimmer/ShimmerOneCard';
import ShimmerOneCardMobile from 'bundles/unified-home-common/components/loading-shimmer/ShimmerOneCardMobile';
import {
  getCompletedStateCookie,
  setCompletedStateCookie,
} from 'bundles/unified-home-common/utils/pathwayCompletedStateCookieUtils';
import removeKeyFromQueryString from 'bundles/unified-home-common/utils/removeKeyFromQueryString';

import _t from 'i18n!nls/consumer-home';

const styles = {
  container: (isContainerVisible: boolean) => css`
    position: fixed;
    width: 100%;
    height: 100%;
    top: 118px;
    left: 0;
    z-index: 10000;
    background: var(--cds-color-white-0);
    opacity: 1;
    opacity: ${isContainerVisible ? 1 : 0};
    visibility: ${isContainerVisible ? 'visible' : 'hidden'};
    pointer-events: ${isContainerVisible ? 'all' : 'none'};
    transition: all 2s ease-out;
  `,

  titleContainer: css`
    margin: var(--cds-spacing-300) 0 var(--cds-spacing-100);
    display: flex;
    align-items: center;
    justify-content: center;
  `,

  title: css`
    margin-left: var(--cds-spacing-100);
  `,
};

const PageShimmer = () => {
  const [stage, setStage] = useState(0);
  const [isContainerVisible, setIsContainerVisible] = useState(true);
  const location = useLocation();
  const { query } = location;
  const navigate = useNavigate();
  const userAgent = useUserAgent();
  const skillRoles = useSkillRoles();
  const isLearnerProfileLoading = useIsLearnerProfileLoading();
  const completedOnboarding = location.query?.completedOnboarding;
  const isInLegalConsolidationFlowAndHasTouQueryParam = useIsInLegalConsolidationFlowAndHasTouQueryParam();

  const onAnimationEnd = useCallback<() => void>(() => {
    setTimeout(() => {
      setIsContainerVisible(false);

      setTimeout(() => {
        // Clear the query params after the shimmer animation is done
        navigate(
          {
            pathname: location.pathname,
            search: removeKeyFromQueryString(query, 'completedOnboarding'),
          },
          { replace: true }
        );
      }, 2000);
    }, 200);
  }, [navigate, location.pathname, query]);

  const startAnimation = useCallback<() => void>(() => {
    setTimeout(() => {
      setStage(1);
      setTimeout(() => {
        setStage(2);

        if (window.innerWidth > breakpoints.values.md) {
          setTimeout(() => {
            setStage(3);

            onAnimationEnd();
          }, 1000);
        } else {
          onAnimationEnd();
        }
      }, 1000);
    }, 200);
  }, [onAnimationEnd]);

  // Ensure main content is not scrollable when shimmer is visible
  useEffect(() => {
    if (completedOnboarding && isContainerVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [completedOnboarding, isContainerVisible]);

  useEffect(() => {
    if (!inServerContext && completedOnboarding && !isInLegalConsolidationFlowAndHasTouQueryParam) {
      startAnimation();
    }
  }, [startAnimation, completedOnboarding, isInLegalConsolidationFlowAndHasTouQueryParam]);

  useEffect(() => {
    // Clear a users completed pathway state if they have completed onboarding and completed state is active.
    if (completedOnboarding) {
      const pathwayCompletedStateCookie = getCompletedStateCookie();
      const userId = String(userGet?.().id);

      if (pathwayCompletedStateCookie?.showCompletedState && userId === pathwayCompletedStateCookie?.userId) {
        setCompletedStateCookie({ showCompletedState: false, s12nId: pathwayCompletedStateCookie?.s12nId, userId });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!completedOnboarding) {
    return null;
  }

  const rolesLength = skillRoles?.length ?? 0;
  const DesktopShimmer = rolesLength > 3 ? ShimmerFourCards : ShimmerOneCard;
  const MobileShimmer = rolesLength > 3 ? ShimmerFourCardsMobile : ShimmerOneCardMobile;

  return (
    <div css={styles.container(isContainerVisible)}>
      <PageGridContainer>
        <Grid item xs={12} css={styles.titleContainer}>
          <div>
            <CoachIcon />
          </div>
          <div>
            <Typography2 css={styles.title} variant="bodyPrimary" component="p">
              {_t('Preparing your recommendations')}
            </Typography2>
          </div>
        </Grid>

        {!isLearnerProfileLoading && (
          <Grid item xs={12}>
            <Media query={{ maxWidth: breakpoints.values.md - 1 }} defaultMatches={userAgent?.isMobileBrowser}>
              {(isMobileScreen) =>
                isMobileScreen ? <MobileShimmer stage={stage} /> : <DesktopShimmer stage={stage} />
              }
            </Media>
          </Grid>
        )}
      </PageGridContainer>
    </div>
  );
};

export default PageShimmer;
